import { useQuery } from "@tanstack/react-query";
import { useForm, useWatch } from "react-hook-form";

import type { IqbiProjectConfigurationDto } from "@/api/types";
import { Breadcrumbs } from "@/components/Breadcrumbs/Breadcrumbs";
import { ErrorPage } from "@/components/Error/ErrorPage";
import { Form } from "@/components/Form/Form";
import { FormCheckbox } from "@/components/Form/FormCheckbox";
import { FormInput } from "@/components/Form/FormInput";
import { DocumentPage } from "@/components/Page/DocumentPage";
import { Button } from "@/design-system/Button";
import { FormField } from "@/design-system/FormField";
import { FullSizeLoader } from "@/design-system/FullSizeLoader";
import { createRequiredStringRule } from "@/helpers/rules";
import { usePermission } from "@/hooks/usePermission";
import { useSlug } from "@/hooks/useSlug";
import { integrationMutations } from "@/queries/integrations/mutations";
import { useIntegrationQueries } from "@/queries/integrations/queryOptions";
import { routes } from "@/routes";
import { useTranslation } from "@/translations";

export type IqbiFormValues = IqbiProjectConfigurationDto;

export function IqbiPage(): React.ReactNode {
  const { t } = useTranslation();
  const slug = useSlug();
  const hasPermission = usePermission();
  const integrationQueries = useIntegrationQueries();

  const { data, isFetching, error } = useQuery(integrationQueries.iqbiConfiguration());
  const mutation = integrationMutations.useEditIqbiConfiguration();

  if (!hasPermission((x) => x.integrations.canManageIqbi)) {
    return <ErrorPage status={404} />;
  }

  if (error) {
    return <ErrorPage error={error} />;
  }

  if (isFetching) {
    return <FullSizeLoader withPadding />;
  }

  const name = t("page.integration.iqbi.title");

  return (
    <DocumentPage
      containerBoundary="inherit"
      contentSize="constrained"
      tabTitle={name}
      breadcrumbs={
        <Breadcrumbs
          pages={[{ name: t("page.integration.title"), to: routes.projects.integrations({ slug }) }, { name }]}
        />
      }
    >
      <IqbiForm
        defaultValues={{
          enabled: data?.enabled || false,
          credentials: data?.credentials || "",
          locationUuid: data?.locationUuid || "",
        }}
        isSubmitting={mutation.isPending}
        onSubmit={mutation.mutate}
      />
    </DocumentPage>
  );
}

function IqbiForm({
  defaultValues,
  isSubmitting,
  onSubmit,
}: {
  defaultValues: Partial<IqbiFormValues>;
  isSubmitting: boolean;
  onSubmit: (data: IqbiFormValues) => void;
}): React.ReactNode {
  const { t } = useTranslation();
  const formMethods = useForm<IqbiFormValues>({ defaultValues });
  const enabled = useWatch({ control: formMethods.control, name: "enabled" });

  return (
    <Form.Root formMethods={formMethods} onSubmit={onSubmit}>
      <Form.Container title={t("page.integration.iqbi.title")}>
        <Form.Body>
          <FormCheckbox<IqbiFormValues, "enabled">
            name="enabled"
            label={t("common.action.add")}
            onChange={(e) => {
              if (enabled && !e.target.checked) {
                formMethods.clearErrors();
              }
            }}
          />

          <FormField label={t("page.integration.iqbi.form.credentials")} required>
            <FormInput<IqbiFormValues, "credentials">
              name="credentials"
              rules={{
                validate: {
                  required: enabled
                    ? createRequiredStringRule(t, "page.integration.iqbi.form.credentials")
                    : () => undefined,
                },
              }}
              type="password"
              placeholder="username:password"
              disabled={!enabled}
            />
          </FormField>

          <FormField label={t("page.integration.iqbi.form.location-uuid")} required>
            <FormInput<IqbiFormValues, "locationUuid">
              name="locationUuid"
              rules={{
                validate: {
                  required: enabled
                    ? createRequiredStringRule(t, "page.integration.iqbi.form.location-uuid")
                    : () => undefined,
                },
              }}
              placeholder="00000000-0000-0000-0000-000000000000"
              disabled={!enabled}
            />
          </FormField>
        </Form.Body>
        <Form.Footer
          renderPrimaryAction={() => (
            <Button type="submit" isLoading={isSubmitting}>
              {t("common.action.save")}
            </Button>
          )}
        />
      </Form.Container>
    </Form.Root>
  );
}
